import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

const ProductGallery = () => {
  const galleryImages = [
    {
      url: '/site-assets/hero.jpg',
      title: 'Shriram Spectape Product Range',
      description: 'Complete collection of specialty adhesive tapes'
    },
    {
      url: '/site-assets/poly1.jpg',
      title: 'Polyester Tapes',
      description: 'High-temperature masking and insulation solutions'
    },
    {
      url: '/site-assets/poly2.jpg',
      title: 'Double-Sided Polyester Tape',
      description: 'Premium bonding strength for industrial applications'
    },
    {
      url: '/site-assets/poly3.jpg',
      title: 'Electrical Insulation Tapes',
      description: 'Superior thermal resistance up to 260°C'
    },
    {
      url: '/site-assets/epdm.jpg',
      title: 'EPDM Foam Tape',
      description: 'Excellent weatherproofing and sealing'
    },
    {
      url: '/site-assets/neo.jpg',
      title: 'Neoprene Foam Tape',
      description: 'Shock absorption and vibration reduction'
    },
    {
      url: '/site-assets/nitrile.jpg',
      title: 'Nitrile Foam Tape',
      description: 'Oil and heat resistant for HVAC applications'
    },
    {
      url: '/site-assets/polyimide.png',
      title: 'Polyimide Tape (Kapton)',
      description: 'Exceptional thermal stability for extreme environments'
    },
    {
      url: '/site-assets/aluminum.jpg',
      title: 'Aluminum Foil Tape',
      description: 'Heat conductivity for HVAC and thermal insulation'
    },
    {
      url: '/site-assets/cross.png',
      title: 'Cross Filament Tape',
      description: 'Fiberglass reinforced for heavy-duty applications'
    },
    {
      url: '/site-assets/glass.png',
      title: 'Glass Cloth Tape',
      description: 'High mechanical strength for industrial environments'
    },
    {
      url: '/site-assets/duct.png',
      title: 'Duct Tape',
      description: 'Versatile solution for repairs and sealing'
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, galleryImages.length]);

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex === galleryImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? galleryImages.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  return (
    <div className="relative w-full h-[600px] bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.05) 35px, rgba(255,255,255,.05) 70px)'
        }}></div>
      </div>

      {/* Main Image */}
      <div className="relative h-full flex items-center justify-center">
        <div className="relative w-full max-w-6xl mx-auto px-4">
          <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="relative h-[400px]">
              {galleryImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ease-in-out ${
                    index === currentIndex 
                      ? 'opacity-100 scale-100' 
                      : 'opacity-0 scale-95'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.title}
                    className="w-full h-full object-contain p-8"
                  />
                </div>
              ))}
            </div>

            {/* Image Info Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-8">
              <div className="text-white transition-all duration-500">
                <h3 className="text-2xl font-bold mb-2">{galleryImages[currentIndex].title}</h3>
                <p className="text-gray-200">{galleryImages[currentIndex].description}</p>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <Button
            onClick={goToPrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg"
            variant="outline"
          >
            <ChevronLeft className="h-6 w-6 text-gray-900" />
          </Button>
          <Button
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg"
            variant="outline"
          >
            <ChevronRight className="h-6 w-6 text-gray-900" />
          </Button>
        </div>
      </div>

      {/* Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
        {galleryImages.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all duration-300 rounded-full ${
              index === currentIndex
                ? 'w-12 h-3 bg-orange-600'
                : 'w-3 h-3 bg-white/50 hover:bg-white/80'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Corner Decorations */}
      <div className="absolute top-0 left-0 w-32 h-32 bg-orange-600/10 rounded-br-full"></div>
      <div className="absolute bottom-0 right-0 w-32 h-32 bg-orange-600/10 rounded-tl-full"></div>
    </div>
  );
};

export default ProductGallery;
